import { SHOP_BREAKPOINTS } from "@/components/organisations/shop/shopTheme";
import { theme } from "@/core/theme";

/**
 * Root app theme responsive arrays (640 / 960 / 1280 / 1600).
 * Index 0: below 640px | index 1: 640–959px (same tokens as index 0) | index 2: 960–1279px | index 3: 1280px+.
 */
export function lhDashboardBp(below960, between960And1280, from1280) {
  return [below960, below960, between960And1280, from1280];
}

/** Five-tier page inset arrays (<960 | 960–1279 | 1280–1599 | 1600+); indices 0–1 match below960. */
export function lhDashboardPageInset(below960, between960And1280, between1280And1600, from1600) {
  return [below960, below960, between960And1280, between1280And1600, from1600];
}

/** Webinar cards: stacked layout only below 640px; row layout from 640px (still dropdown nav until 960px). */
export const learningHubDashboardWebinarCardStacked = [true, false, false, false];

/** Shared typography and spacing for learning hub dashboard sections (mobile → desktop). */
export const dashboardPageMaxWidth = 1920;
/** Horizontal inset for CMP dashboard page content (aligns with home dashboard masonry). */
export const dashboardPagePx = theme.spacing.panelBox.px;
export const dashboardMainStackMaxWidth = 1280;
export const dashboardMainStackGap = [40, 40, 80];
/** Vertical gap between dashboard sections (root theme breakpoints). */
export const learningHubDashboardMainStackGap = lhDashboardBp(40, 40, 80);
/** Gap between side nav / filters and main content (mobile, tablet, desktop). */
export const dashboardSidebarContentGap = [40, 48, 60];

/** Inner padding for sidebars (nav, filter panels): tablet 18px, desktop 20px. */
export const dashboardSidebarPadding = [18, 18, 20];

/** Vertical gap between major sidebar sections: tablet 32px, desktop 40px. */
export const dashboardSidebarGap = [32, 32, 40];

/** Sidebar filter panel title (e.g. “Filters”): tablet 28px, desktop 32px. */
export const dashboardSidebarFiltersTitleFontSize = [28, 28, 32];

/** Shop list page heading (e.g. “Find a matching test”): tablet 36px, desktop 44px. */
export const dashboardShopListTitleFontSize = [28, 36, 44];
export const dashboardShopListTitleSubtitleGap = 16;
export const dashboardShopListSubtitleFontSize = 16;
/** Space between shop tab switcher and list page title: tablet 32px, desktop 40px. */
export const dashboardShopListSwitcherTitleGap = [32, 32, 40];
/** Space between list page subtitle and search/controls row: tablet 48px, desktop 60px. */
export const dashboardShopListSubtitleControlsGap = [48, 48, 60];
/** Space between search/controls row and list content (matches shop tests page). */
export const dashboardShopListControlsToContentGap = [20, 20, 40];

/** Max items shown per resource section on the learning hub dashboard. */
export const dashboardSectionListMaxItems = 6;

/** Max upcoming events in the learning hub dashboard carousel. */
export const dashboardEventsMaxItems = 10;

/** Dashboard carousels (events, courses) horizontal inset (<640 | 640–959 | 960–1279 | 1280+). */
export const learningHubDashboardCarouselInset = [60, 40, 80, 40];

/**
 * Lunch & Learns text column padding (top → right → bottom → left).
 * Below 1600: prior dashboard tiers; 1600+: 60, 100, 80, 80.
 */
export const learningHubDashboardLunchAndLearnsContentPt = lhDashboardPageInset(40, 48, 60, 60);
export const learningHubDashboardLunchAndLearnsContentPr = lhDashboardPageInset(40, 48, 40, 100);
export const learningHubDashboardLunchAndLearnsContentPb = lhDashboardPageInset(60, 68, 80, 80);
export const learningHubDashboardLunchAndLearnsContentPl = lhDashboardPageInset(40, 48, 40, 80);

/** Outer padding for CMP shop and learning hub list layout (themed pages use shop breakpoints). */
export const dashboardMainLayoutPt = [68, 80, 96];
export const dashboardMainLayoutPb = [80, 90, 120];
export const dashboardMainLayoutPx = [16, 28, 40];

/**
 * Learning hub dashboard page inset (root theme: 640 / 960 / 1280 / 1600).
 * Below 960: pt 68, pb 80, px 16 | 960–1279: pt 80, pb 96, px 28 | 1280–1599: same as 960–1279 |
 * 1600+: pt 96, pb 120, px 40
 */
export const learningHubDashboardMainLayoutPt = lhDashboardPageInset(68, 80, 80, 96);
export const learningHubDashboardMainLayoutPb = lhDashboardPageInset(80, 96, 96, 120);
export const learningHubDashboardMainLayoutPx = lhDashboardPageInset(16, 28, 28, 40);

/** Learning hub dashboard (root theme): side nav from 960px; dropdown only below. */
export const learningHubDashboardSidebarDesktopMinWidth = theme.breakpoints.md;

/** Dashboard layout: column below 960px; side nav left and main content right from 960px. */
export const learningHubDashboardSidebarContentFlexDirection = lhDashboardBp(
  "column",
  "row",
  "row"
);

export const learningHubDashboardSidebarContentGap = lhDashboardBp(40, 48, 60);

/** Tablet and up: side nav / desktop filters (matches CMP shop layout). */
export const dashboardSidebarDesktopMinWidth = SHOP_BREAKPOINTS.sm;
export const dashboardSidebarDesktopMediaQuery = `@media screen and (min-width: ${dashboardSidebarDesktopMinWidth})`;

/** Below 1152px: stacked; tablet and desktop: side nav and content in a row. */
export const dashboardSidebarContentFlexDirection = ["column", "row", "row"];

export const dashboardSectionTitle = {
  fontFamily: "gilroyBold",
  fontSize: [24, 24, 36],
  minWidth: 0
};

export const dashboardSectionBody = {
  fontFamily: "gilroyMedium",
  fontSize: [16, 16, 18],
  lineHeight: ["24px", "24px", "28px"]
};

export const dashboardSectionLabel = {
  fontFamily: "gilroyBold",
  fontSize: [16, 16, 18],
  lineHeight: ["24px", "24px", "28px"]
};

export const dashboardPanelPadding = {
  px: theme.spacing.panelBox.px,
  pt: theme.spacing.panelBox.pt,
  pb: theme.spacing.panelBox.pb
};

export const dashboardHeaderIconCircleSize = [52, 52, 60];
/** Inner icon pixel size (SVG components do not accept responsive arrays). */
export const dashboardHeaderIconSize = 30;

export const dashboardInnerStackGap = [20, 20, 40];
export const dashboardHeaderRowGap = [16, 16, 20];
/** Space between panel title row and body content (desktop 28px). */
export const dashboardPanelTitleContentGap = [10, 10, 28];
export const dashboardCardGridGap = [16, 16, 20];

/** ButtonV2 size: smaller on mobile/tablet, default md on desktop. */
export const dashboardActionButtonSize = ["sm", "sm", "md"];
